import { ArticleType } from '@wix/answers-api';

import { AttachingMethod } from '../../../types';
import { CategoryItemType } from '../../common/api/getCategories';
import { AttachmentErrorType } from '../../common/reducers/types';
import { Page } from '../../common/types';
import { ButtonClicked } from './buttonClicked';
import { SupportTypeArticle } from './solutionInfo';

export type SearchTryDifferentBiData = Readonly<{
  searchedText: string;
}>;

export type SearchSuggestedQuestionBiData = Readonly<{
  searchedText: string;
}>;

export type BreadcrumbsClickBiData = Readonly<{
  isHomeCategory: boolean;
  id: string;
  index: number;
  name?: string;
}>;

export enum SolutionTypes {
  ARTICLE = 'article',
}

export type ChatbotClickOnSolutionBiData = Readonly<{
  solutionType: SolutionTypes;
  solutionId: string;
}>;

export type ChatbotNotificationsBiData = Readonly<{
  counterValue: number;
}>;

export type ChatbotWarningBiData = Readonly<{
  timeWaited: number; // seconds
  messageText: string;
  messageId: string;
}>;

export type SchedulerBiData = Readonly<{
  formattedTimeslot: string;
}>;

export type StatusBannerBiData = Readonly<{
  incident_name?: string;
  incident_severity?: string;
  incident_state?: string;
  banner_type?: string;
  banner_name?: string;
  banner_text?: string;
}>;

export type CategoryBiData = Readonly<{
  id: string;
  locale?: string;
}>;

export type ArticleBiData = Readonly<{
  id: string;
  locale?: string;
  title?: string;
  url?: string;
  distance?: number;
  articleType?: SupportTypeArticle;
}>;

export type HelpResourceBiData = Readonly<{
  url?: string;
  text: string;
  itemPosition: number;
  blockPosition: number;
  buttonClickedBiName: ButtonClicked;
}>;

export type PositionBiData = Readonly<{
  position: number;
}>;

export type ButtonBiData = PositionBiData &
  Readonly<{
    text: string;
    url?: string;
  }>;

export type ChatbotLinkBiData = Readonly<{
  isCta: boolean;
  link: string;
  label: string | null;
  buttonType?: string;
}>;

export type CategoryBadgeClick = Readonly<{
  articleId: string;
  categoryId: string;
  categoryName: string;
  distance?: number;
  position: number;
}>;

export type AnswerGenSummarizedAnsLinkClick = Readonly<{
  url: string;
}>;
export type AnswerGenBasedOnArticleClick = Readonly<{
  articleId: string;
  url: string;
}>;

export type AnswerGenMediaClick = Readonly<{
  url: string;
}>;

export type AnswerGenNoHelpfulReasonClick = Readonly<{
  clickedValue: string;
}>;

export type CategoriesActionBiData = PositionBiData &
  Readonly<{
    category: CategoryItemType;
  }>;

export type ArticleActionBiData = PositionBiData &
  Readonly<{
    id: string;
    categoryId?: string;
    distance?: number;
    articleType: ArticleType;
    summarizedAnswerBasedOn?: string;
    summarizedAnswerText?: string;
    summarizedAnswerType?: string;
    summarizedAnswerWordsCount?: number;
  }>;

export type SearchSuggestedQuestionInArticleClick = Readonly<{
  id: string;
  searchedText: string;
}>;

export type ArticleTabActionBiData = Readonly<{
  tabName: string;
  tabIndex: number;
  articleId: string;
}>;

export type MessageNotificationClickedBiData = CurrentPageBiData &
  Readonly<{
    clickedValue: string;
  }>;

export type ArticleAnchorClickedBiData = ArticleBiData &
  Readonly<{
    clickedValue: string;
  }>;

export type ArticleImageClickBiData = Readonly<{
  articleId: string;
  imageSrc: string;
}>;

export type ArticleSectionActionBiData = Readonly<{
  expand: boolean;
  linkText: string;
  articleId: string;
}>;

export type ArticleSolutionLinkActionBiData = Readonly<{
  article: ArticleBiData;
  isExternalLink: boolean;
  linkText: string;
  linkSectionText?: string;
  linkUrl?: string;
  isCta?: boolean;
}>;

export type ArticleVoteActionBiData = Readonly<{
  articleId: string;
  buttonClicked:
    | ButtonClicked.FeatureRequestVote
    | ButtonClicked.FeatureRequestUnvote
    | ButtonClicked.FeatureRequestFollow
    | ButtonClicked.FeatureRequestUnfollow
    | ButtonClicked.KnownIssueVote
    | ButtonClicked.KnownIssueUnvote;
}>;

export type CurrentPageBiData = Readonly<{
  currentPage: Page;
}>;

export type ErrorMessagePresentedBiData = Readonly<{
  messageText: string;
  messageId: string;
  errorType?: string;
  isTryAgainAvailable?: boolean;
  buttonName?: 'try_again';
}>;

export enum SEND_ERROR_TYPE {
  NETWORK_ERROR = 'network_error',
  TIMEOUT_ERROR = 'timeout_error',
  TECH_ERROR = 'tech_error',
}

export type SendErrorAppearedBiData = Readonly<{
  messageText: string | undefined;
  messageId: string | undefined;
  errorType: SEND_ERROR_TYPE;
}>;

export type ConversationStartOverBiData = Readonly<{
  destinationConversationId?: string;
}>;

export type AttachmentErrorBiData = Readonly<{
  validFiles: File[];
  invalidFiles: File[];
  attachingMethod: AttachingMethod;
  errorType: AttachmentErrorType;
}>;

export enum ChatbotSendActionType {
  Button = 'button', // regular chatbot message button
  CategoryButton = 'category_button',
  SelectorButton = 'selector_button',
  SubmitButton = 'submit_button', // submit button near field input
  Enter = 'enter',
}

export type ChatbotSendMessageBiData = Readonly<{
  sendActionType: ChatbotSendActionType;
}>;

export type AnswerFeedbackPresentedBiData = Readonly<{
  messageId: string;
}>;

export type AnswerFeedbackClickedBiData = Readonly<{
  messageId: string;
  voteType: 'up' | 'down';
}>;

export type AnswerFeedbackReasonButtonClickedBiData = Readonly<{
  messageId: string;
  reasonType: string;
}>;

export type AnswerFeedbackFreeTextBiData = Readonly<{
  messageId: string;
  text?: string;
  clickType: 'submit' | 'cancel';
}>;

export type InputTextFieldBiData = Readonly<{
  inputFieldText: string | undefined;
}>;

export type ArticleExternalLinkActionBiData = ArticleBiData &
  Readonly<{
    isCta?: boolean;
  }>;
